/* ==========================================================================
   Titan Package Protection  (opt-out add-on)

   Adds the Shipping/Package Protection product to the cart as a real line
   item BY DEFAULT, and keeps it in sync with a toggle in the cart drawer.
   The customer can opt out (toggle off, or remove the line) and that choice
   is remembered. If the cart is emptied of real products, protection is
   removed too so a lone $3.95 item is never left behind.

   Design goals / safety:
     - Does NOT touch TitanCart.add or Dawn's cart-drawer.js.
     - Reconcile is idempotent: it only mutates the cart when the cart does
       not already match the saved preference, so over-firing is harmless.
     - Our mutations use /cart/add.js and /cart/change.js directly (NOT
       TitanCart.add), so they never re-trigger the add pipeline -> no loop.
     - Markup is re-rendered by swapping section HTML WITHOUT opening the
       drawer, so page-load reconcile stays silent.
     - Toggle is bound by event delegation, so drawer re-renders never drop it.
   ========================================================================== */
(function () {
  'use strict';

  if (window.TitanPkgProtect) return;

  var PREF_KEY = 'titan_pkg_protect';     // 'on' | 'off'  (unset = on)
  var DEBOUNCE_MS = 150;

  var busy = false;          // reconcile in flight
  var selfChange = false;    // we initiated the current protection change
  var ignoreMutations = false;
  var lastHadProt = null;    // track present->absent transitions (native removal)
  var debounceTimer = null;

  /* ---------- preference ---------- */
  function getPref() {
    try {
      var v = window.localStorage.getItem(PREF_KEY);
      return v === 'off' ? 'off' : 'on'; // default ON
    } catch (e) { return 'on'; }
  }
  function setPref(v) {
    try { window.localStorage.setItem(PREF_KEY, v === 'off' ? 'off' : 'on'); } catch (e) {}
  }

  /* ---------- config (variant id from the toggle element) ---------- */
  function variantId() {
    var el = document.querySelector('[data-titan-pkg-protect]');
    if (el) {
      var id = parseInt(el.getAttribute('data-sp-variant'), 10);
      if (id) return id;
    }
    return window.TITAN_PKG_PROTECT_VARIANT || null;
  }
  function isProt(item) {
    var vid = variantId();
    return vid && item && item.variant_id === vid;
  }

  /* ---------- section rendering (swap, never open) ---------- */
  function sectionsToRequest() {
    var ids = [];
    if (document.querySelector('cart-drawer')) ids.push('cart-drawer');
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    if (document.querySelector('cart-notification')) ids.push('cart-notification');
    return ids;
  }
  function innerFrom(html, selector) {
    if (!html) return null;
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el = doc.querySelector(selector || '.shopify-section');
      return el ? el.innerHTML : null;
    } catch (e) { return null; }
  }
  function applySections(sections) {
    if (!sections) return;
    ignoreMutations = true;
    if (sections['cart-drawer']) {
      try {
        var doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        var freshInner = doc.querySelector('#CartDrawer');
        var liveInner = document.querySelector('#CartDrawer');
        if (freshInner && liveInner) liveInner.innerHTML = freshInner.innerHTML;
        // Sync ONLY the `is-empty` class, so an emptied cart shows Dawn's empty
        // state (and a refilled cart drops it) WITHOUT disturbing the drawer's
        // open-state classes (animate/active) -- overwriting the whole className
        // would strip those and close the drawer while the shopper is looking.
        var freshWrap = doc.querySelector('cart-drawer');
        var liveWrap = document.querySelector('cart-drawer');
        if (freshWrap && liveWrap) {
          if (freshWrap.classList.contains('is-empty')) liveWrap.classList.add('is-empty');
          else liveWrap.classList.remove('is-empty');
        }
        moveProtectionLast();
      } catch (e) {
        var fallback = innerFrom(sections['cart-drawer'], '#CartDrawer');
        var liveFb = document.querySelector('#CartDrawer');
        if (fallback !== null && liveFb) liveFb.innerHTML = fallback;
      }
    }
    if (sections['cart-icon-bubble']) {
      var b = innerFrom(sections['cart-icon-bubble']);
      var liveB = document.getElementById('cart-icon-bubble');
      if (b !== null && liveB) liveB.innerHTML = b;
    }
    // release the mutation guard after the DOM settles
    window.setTimeout(function () { ignoreMutations = false; }, 60);
  }

  /* ---------- cart ops ---------- */
  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }
  function mutate(url, body) {
    body.sections = sectionsToRequest().join(',');
    body.sections_url = window.location.pathname + window.location.search;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }
  function addProt() {
    selfChange = true;
    return mutate('/cart/add.js', { items: [{ id: variantId(), quantity: 1 }] })
      .then(function (state) { applySections(state && state.sections); })
      .finally(function () { selfChange = false; });
  }
  function setProtQty(q) {
    selfChange = true;
    return mutate('/cart/change.js', { id: String(variantId()), quantity: q })
      .then(function (state) { applySections(state && state.sections); })
      .finally(function () { selfChange = false; });
  }

  /* ---------- toggle UI sync ---------- */
  function syncToggle(on) {
    var cb = document.querySelector('[data-titan-pkg-toggle]');
    if (cb) cb.checked = !!on;
  }

  /* Shopify lists the newest line first, so protection (added last) lands on
     top. Pin its row to the bottom of the cart table instead. Presentation
     only; self-terminates (no-ops once it is already last). */
  function moveProtectionLast() {
    var row = document.querySelector('#CartDrawer .titan-pkg-line');
    if (!row || !row.parentNode) return false;
    var tbody = row.parentNode;
    if (tbody.lastElementChild === row) return false;
    ignoreMutations = true;
    tbody.appendChild(row);
    window.setTimeout(function () { ignoreMutations = false; }, 60);
    return true;
  }

  /* ---------- reconcile ---------- */
  function reconcile() {
    if (busy || !variantId()) return Promise.resolve();
    busy = true;
    return getCart().then(function (cart) {
      var items = (cart && cart.items) || [];
      var prot = null, realCount = 0;
      for (var i = 0; i < items.length; i++) {
        if (isProt(items[i])) prot = items[i];
        else realCount += items[i].quantity;
      }
      var hasProt = !!prot;

      // Native removal (clicked the line's X while protection was on):
      // honor it as an opt-out instead of re-adding.
      if (lastHadProt === true && !hasProt && !selfChange && realCount > 0) {
        setPref('off');
      }
      lastHadProt = hasProt;

      // Never leave protection alone in an otherwise-empty cart.
      // Reset the transition flag here: once the cart is empty, a later add
      // must not be misread as the shopper having removed protection.
      if (realCount === 0) {
        lastHadProt = false;
        syncToggle(getPref() === 'on');
        if (hasProt) return setProtQty(0).then(function () { lastHadProt = false; });
        return;
      }

      var pref = getPref();
      if (pref === 'on' && !hasProt) { syncToggle(true); return addProt().then(function () { lastHadProt = true; }); }
      if (pref === 'off' && hasProt) { return setProtQty(0).then(function () { lastHadProt = false; syncToggle(false); }); }
      if (hasProt && prot.quantity !== 1) { return setProtQty(1); }
      syncToggle(pref === 'on' && hasProt);
      return;
    }).catch(function (e) {
      console.warn('[TitanPkgProtect] reconcile failed', e);
    }).finally(function () { busy = false; });
  }

  function reconcileDebounced() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(reconcile, DEBOUNCE_MS);
  }

  /* ---------- wiring ---------- */

  // 1) After a TitanCart add (user added a product and the drawer opened).
  document.addEventListener('titan:cart:updated', function () { reconcileDebounced(); });

  // 2) Toggle changes (delegated so drawer re-renders keep working).
  document.addEventListener('change', function (e) {
    var cb = e.target && e.target.closest ? e.target.closest('[data-titan-pkg-toggle]') : null;
    if (!cb) return;
    setPref(cb.checked ? 'on' : 'off');
    reconcile();
  });

  // 3) Dawn removals / qty changes inside the drawer (no custom event fired),
  //    plus our own swaps (guarded by ignoreMutations).
  function observeDrawer() {
    var target = document.querySelector('#CartDrawer');
    if (!target || !window.MutationObserver) return;
    var mo = new MutationObserver(function () {
      if (ignoreMutations) return;
      moveProtectionLast();
      reconcileDebounced();
    });
    mo.observe(target, { childList: true, subtree: true });
  }

  // 4) Page load: ensure protection matches preference even if the shopper
  //    never opens the drawer (covers the cart page / direct-to-checkout).
  function boot() {
    observeDrawer();
    reconcile().then(moveProtectionLast);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.TitanPkgProtect = { reconcile: reconcile, getPref: getPref, setPref: setPref };
})();
